// src/components/ReviewPanel.js
//
// G8.2 (2026-09-14, task list section 223): src\data\reviewRecord.js - ruling 69, built 2026-08-29,
// 38 assertions green in TST-1 - reaches a screen. Two faces of one record:
//   <ReviewPanel claim user />            on the claim page: a General Chairperson (or admin) records
//                                         a review of this claim WITH ITS REASON (69.2: transparent,
//                                         recorded, the reason); the reviews already on record are
//                                         listed under it.
//   <ReviewsOfMyOffice user />            on the Local Chairperson's desk: every review of his
//                                         office's work, so the review is never silent (69.2's first
//                                         condition). Read-only.
// Who may review, and why not, is the module's (mayReview / reviewBlockedReason); the Firestore
// rules for /reviewRecords hold the same line and pin the record's shape. This file decides nothing.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc } from 'firebase/firestore';
import { mayReview, reviewBlockedReason, reviewRecord, reviewsOfOffice, reviewLine, REASON_MAX, SUBJECTS } from '../data/reviewRecord';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { capScopes } from '../data/scopeCap';   // T5 item 20: the 30-territory cap, in one place

const C = { panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2' };

// A query the read rule can prove: the GC by committee, the LC by his offices, admin unshaped.
function reviewQueries(user, extra) {
  const base = collection(db, 'reviewRecords');
  const withExtra = (...c) => query(base, ...c, ...(extra || []));
  if (!user) return [];
  if (user.isAdmin) return [withExtra()];
  if (user.role === 'general_chair' && user.committee) return [withExtra(where('committee', '==', user.committee))];
  if (user.role === 'local_chair' && Array.isArray(user.scopes) && user.scopes.length) {
    return capScopes(user).scopes.map(s => withExtra(where('subordinateOffice', '==', s)));
  }
  return [];
}

function useReviews(user, extra) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const key = (user && user.uid) + '|' + (user && user.role) + '|' + (user && user.committee) + '|' + ((user && user.scopes) || []).join(',') + '|' + JSON.stringify(extra ? extra.length : 0);
  useEffect(() => {
    const qs = reviewQueries(user, extra);
    if (!qs.length) { setRows([]); return undefined; }
    const per = qs.map(() => null);
    const unsubs = qs.map((q, i) => onSnapshot(q, snap => {
      per[i] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (per.every(x => x !== null)) { const m = new Map(); per.flat().forEach(r => m.set(r.id, r)); setRows(Array.from(m.values())); setErr(''); }
    }, e => { setErr('Reviews could not be read: ' + (e && e.code ? e.code : e.message)); setRows([]); }));
    return () => unsubs.forEach(u => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return [rows, err];
}

export function ReviewPanel({ claim, user }) {
  const subject = useMemo(() => claim ? { ...claim, id: claim.id || claim.docId } : null, [claim]);   // the claim NUMBER names the review; the doc id is the fallback
  const extra = useMemo(() => subject && subject.id ? [where('subjectId', '==', subject.id)] : null, [subject && subject.id]);
  const [rows, err] = useReviews(user, extra);
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  if (!subject) return null;
  const may = mayReview(subject, user);
  const blocked = may ? '' : reviewBlockedReason(subject, user);
  const record = async () => {
    setErrors([]); setSaved(false);
    const r = reviewRecord(subject, user, { reason, subjectType: SUBJECTS.CLAIM });
    if (!r.ok) { setErrors(r.errors); return; }
    setSaving(true);
    try { await addDoc(collection(db, 'reviewRecords'), r.record); setReason(''); setSaved(true); }
    catch (e) { setErrors(['Not recorded: ' + (e && e.code ? e.code : e.message)]); }
    setSaving(false);
  };
  const sorted = (rows || []).slice().sort((a, b) => String(b.reviewedAt || '') < String(a.reviewedAt || '') ? -1 : 1);
  // A Local Chairperson reads reviews of his office here too; a member sees nothing of this block.
  if (!may && !(user && (user.role === 'local_chair' || user.isAdmin || user.role === 'general_chair'))) return null;
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header"><div><div className="card-title">Review record</div><div className="card-subtitle">A review is not a read. It is an act, and it leaves a record - who, what, when, and why. The officer whose work this is reads it.<RulingRef user={user} n={69} why="A review by a superior office is an act that leaves a record the reviewed officer can read." /></div></div></div>
      {may ? (
        <div style={{ marginBottom: 12 }}>
          <textarea aria-label="Why you are reviewing this (8-400 characters, required)" className="form-input" rows={2} maxLength={REASON_MAX} placeholder="Why you are reviewing this (8-400 characters, required)" value={reason} onChange={e => setReason(e.target.value)} style={{ width: '100%' }} />
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 6 }}>
            <button className="btn btn-primary btn-sm" disabled={saving || !reason.trim()} onClick={record}>Record this review</button>
            {saved ? <span style={{ color: C.ok, fontSize: 12 }}>Recorded.</span> : null}
          </div>
          {errors.length ? <div style={{ color: C.bad, fontSize: 12, marginTop: 6 }}>{errors.map((e, i) => <div key={i}>&#9888; {e}</div>)}</div> : null}
        </div>
      ) : blocked ? <div style={{ fontSize: 12, color: C.dim, marginBottom: 10 }}>{blocked}</div> : null}
      {err ? <div style={{ color: C.bad, fontSize: 12 }}>&#9888; {err}</div> : null}
      {rows === null && !err ? <div style={{ fontSize: 12, color: C.dim }}>Loading…</div> : null}
      {rows && rows.length === 0 && !err ? <div style={{ fontSize: 12, color: C.dim }}>No review of this claim is on record.</div> : null}
      {sorted.map(r => <div key={r.id} style={{ fontSize: 12, color: C.text, padding: '6px 0', borderTop: '1px solid ' + C.line }}>{reviewLine(r)}</div>)}
    </div>
  );
}

export function ReviewsOfMyOffice({ user }) {
  const [rows, err] = useReviews(user, null);
  const mine = useMemo(() => {
    const scopes = (user && user.scopes) || [];
    return scopes.flatMap(s => reviewsOfOffice(rows || [], s));
  }, [rows, user && (user.scopes || []).join(',')]);
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Reviews of your office's work</div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>A review by a superior office is never silent. Every one, with its reason, is listed here.<RulingRef user={user} n={69} why="A review by a superior office is an act that leaves a record the reviewed officer can read." /></div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: mine.length ? C.accent : C.ok, whiteSpace: 'nowrap' }}>{rows === null ? '…' : mine.length + ' on record'}</div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim }}>
        {err ? <div style={{ color: C.bad }}>&#9888; {err}</div> : null}
        {rows && mine.length === 0 && !err ? <div>None. No office has reviewed your work on this platform.</div> : null}
        {mine.map(r => <div key={r.id} style={{ color: C.text, padding: '6px 0', borderTop: '1px solid ' + C.line }}>{reviewLine(r)}</div>)}
      </div>
    </div>
  );
}
